"use client";

import { Menu, Tokens } from "@chakra-ui/react";
import { memo } from "react";
import GenericIcon, {
  type GenericIconType,
} from "./GenericIcon";
import { iconSizePresets } from "@/shared/config/iconSizePresets";
import type { GenericActionTone } from "./actionBar.types";
import { getActionToneColor, getActionToneHoverBg } from "./actionTone";

export type WithMenuItemProps = {
  children: React.ReactNode;
  value: string;
  humanReadable: string;
  menuRole?: "item" | "radio";
  showIndicator?: boolean;
  iconIndicator?: GenericIconType;
  iconIndicatorColor?: Tokens["colors"] | string;
  icon?: GenericIconType;
  iconPosition?: "left" | "right";
  tone?: GenericActionTone;
  width?: string;
  gap?: string;
  flexDirection?: string;
  fontSize?: Tokens["fontSizes"];
  justifyContent?: string;
  hide?: boolean;
  disabled?: boolean;
  onClick?: () => Promise<void> | void;
  closeOnSelect?: boolean;
};

function WithMenuItem({
  children,
  value,
  humanReadable,
  menuRole = "item",
  showIndicator = false,
  iconIndicator = "confirm",
  iconIndicatorColor = "moveon.secondary",
  icon,
  iconPosition = "left",
  tone,
  width,
  gap,
  flexDirection,
  fontSize,
  justifyContent,
  hide = false,
  disabled = false,
  onClick,
  closeOnSelect,
}: WithMenuItemProps) {
  const itemColor = getActionToneColor(tone);
  const hoverBg = getActionToneHoverBg(tone);
  const commonItemProps = {
    display: hide ? "none" : undefined,
    value,
    width,
    gap,
    flexDirection,
    fontSize,
    justifyContent,
    color: itemColor,
    _hover: {
      bg: hoverBg,
      color: itemColor,
    },
    onClick,
    title: humanReadable,
    "aria-label": humanReadable,
    disabled,
    closeOnSelect,
  } as const;

  if (menuRole === "radio") {
    return (
      <Menu.RadioItem key={value} {...commonItemProps}>
        {icon && iconPosition === "left" && (
          <GenericIcon icon={icon} size={iconSizePresets.inline} />
        )}

        {children}

        {/* Right icon (optional) */}
        {icon && iconPosition === "right" && (
          <GenericIcon icon={icon} size={iconSizePresets.inline} />
        )}

        {showIndicator && (
          <Menu.ItemIndicator color={iconIndicatorColor}>
            <GenericIcon icon={iconIndicator} size={iconSizePresets.inline} />
          </Menu.ItemIndicator>
        )}
      </Menu.RadioItem>
    );
  }

  return (
    <Menu.Item key={value} {...commonItemProps}>
      {children}
    </Menu.Item>
  );
}

export default memo(WithMenuItem);
